import React, { useState } from 'react';
import { useShowOrderQuery, useTrackOrderQuery } from '../../app/config/redux/slices/api/orderApiSlice';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { IMAGE_URL } from '../../constants';

const OrderDetails = () => {
    const { orderId } = useParams();
    const [showTrackModal, setShowTrackModal] = useState(false);
    const { userInfo } = useSelector((state) => state.auth);

    // Fetch Order Details
    const { data, isLoading } = useShowOrderQuery({
        id: orderId,
        token: userInfo.access_token,
    });

    const order = data?.data;

    // Fetch Tracking Details (skipped if no AWB)
    const { data: trackingData, isLoading: trackingLoading } = useTrackOrderQuery(
        {
            token: userInfo.access_token,
            awb: order?.shipment?.awb_code,
        },
        {
            skip: !order?.shipment?.awb_code,
        }
    );

    // Destructure tracking info from response
    const trackingActivities = trackingData?.data?.tracking_data?.shipment_track_activities || [];
    const trackUrl = trackingData?.data?.tracking_data?.track_url;

    if (isLoading) return <div className="py-80 text-center">Loading...</div>;

    const isSmallOrder = (order?.items?.length || 0) <= 4;

    // --- COMPONENT SECTIONS ---

    const TrackingModal = () => (
        <>
            <div className={`modal fade ${showTrackModal ? 'show d-block' : ''}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 rounded-20 shadow-lg">
                        <div className="modal-header border-bottom border-gray-100 p-24">
                            <h5 className="modal-title">Live Tracking</h5>
                            <button type="button" className="btn-close shadow-none" onClick={() => setShowTrackModal(false)}></button>
                        </div>
                        <div className="modal-body p-24" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {trackingLoading ? (
                                <div className="text-center py-20">Loading tracking info...</div>
                            ) : trackingActivities.length > 0 ? (
                                <div className="tracking-timeline">
                                    {trackingActivities.map((activity, idx) => (
                                        <div key={idx} className="d-flex gap-16 mb-24 position-relative">
                                            {/* Vertical Line Connector */}
                                            {idx !== trackingActivities.length - 1 && (
                                                <div className="position-absolute" style={{ 
                                                    left: '11px', top: '24px', bottom: '-24px', 
                                                    width: '2px', backgroundColor: '#e9ecef' 
                                                }}></div>
                                            )}
                                            
                                            {/* Dot */}
                                            <div className="flex-shrink-0 mt-4">
                                                <div className={`rounded-circle ${idx === 0 ? 'bg-main-600 shadow-sm' : 'bg-gray-200'}`} 
                                                     style={{ width: '24px', height: '24px', border: '4px solid white', zIndex: 2, position: 'relative' }}>
                                                </div>
                                            </div>

                                            {/* Content */}
                                            <div className="flex-grow-1">
                                                <p className={`mb-4 fw-bold ${idx === 0 ? 'text-main-600' : 'text-heading'}`}>
                                                    {activity['sr-status-label']}
                                                </p>
                                                <p className="text-xs text-gray-500 mb-4">{activity.location}</p>
                                                <p className="text-xs text-gray-400 italic mb-8">{activity.activity}</p>
                                                <span className="text-xs fw-medium text-gray-400 bg-gray-50 px-8 py-4 rounded-4">
                                                    {new Date(activity.date).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-center text-gray-500">No tracking activities found.</p>
                            )}
                        </div>
                        <div className="modal-footer border-top border-gray-100 p-24">
                            {trackUrl && (
                                <a href={trackUrl} target="_blank" rel="noopener noreferrer" className="btn btn-main w-100 py-12 rounded-12">
                                    <i className="ph ph-arrow-square-out me-8"></i> Detailed Tracking Page
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {showTrackModal && <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>}
        </>
    );

    const OrderItemsSection = (
        <div className="bg-white rounded-16 border border-gray-100 shadow-sm overflow-hidden mb-24">
            <div className="p-24 border-bottom border-gray-100">
                <h6 className="mb-0">Items ({order?.items.length})</h6>
            </div>
            <div className="p-24">
                {order?.items.map((item, index) => (
                    <div key={item.id} className={`d-flex flex-column flex-sm-row gap-24 ${index !== order.items.length - 1 ? 'mb-24 pb-24 border-bottom border-gray-50' : ''}`}>
                        <Link to={`/product-details/${item.product.id}`} className="flex-shrink-0 mx-auto mx-sm-0">
                            <div className="rounded-12 border border-gray-100 overflow-hidden bg-gray-50" style={{ width: '100px', height: '100px' }}>
                                <img 
                                    className="w-100 h-100 object-fit-cover"
                                    src={item.product.image ? `${IMAGE_URL}/${item.product.image}` : "/assets/images/thumbs/product-two-img1.png"} 
                                    alt={item.product.name} 
                                />
                            </div>
                        </Link>
                        <div className="flex-grow-1 text-center text-sm-start">
                            <h6 className="text-md mb-8">
                                <Link to={`/product-details/${item.product.id}`} className="hover-text-main-600 transition-1">
                                    {item.product.name}
                                </Link>
                            </h6>
                            <div className="d-flex align-items-center justify-content-center justify-content-sm-start gap-3 flex-wrap">
                                <span className="text-gray-500 text-sm">Qty: <span className="text-heading fw-bold">{item.quantity}</span></span>
                                <span className="text-gray-200">|</span>
                                <span className="text-gray-500 text-sm">Price: <span className="text-heading">₹{item.product.price}</span></span>
                            </div>
                        </div>
                        <div className="text-center text-sm-end">
                            <p className="text-sm text-gray-400 mb-0">Total</p>
                            <p className="text-lg fw-bold text-main-600 mb-0">₹{item.price}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const OrderSummarySection = (
        <div className="bg-white rounded-16 border border-gray-100 shadow-sm p-24 mb-24">
            <div className="d-flex align-items-center justify-content-between mb-20 pb-12 border-bottom border-gray-100">
                <h6 className="mb-0">Order Summary</h6>
                <span 
                    className="px-12 py-6 rounded-pill text-xs fw-bold d-inline-block flex-shrink-0"
                    style={{ 
                        color: order?.order.order_status?.colour || '#7cd992',
                        backgroundColor: `${order?.order.order_status?.colour}20`,
                        border: `1px solid ${order?.order.order_status?.colour}40`
                    }}
                >
                    {order?.order.order_status?.name || 'Pending'}
                </span>
            </div>
            <div className="d-grid gap-12">
                <div className="d-flex justify-content-between text-sm">
                    <span className="text-gray-500">Order ID</span>
                    <span className="fw-bold">#{order?.order.id}</span>
                </div>
                <div className="d-flex justify-content-between text-sm">
                    <span className="text-gray-500">Date</span>
                    <span className="fw-medium text-heading">
                        {order?.order.created_at ? new Date(order.order.created_at).toLocaleDateString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric'
                        }) : '---'}
                    </span>
                </div>
                <div className="d-flex justify-content-between text-sm">
                    <span className="text-gray-500">Delivery Fee</span>
                    <span className="fw-medium text-uppercase text-xs">₹{order?.order.delivery_fee}</span>
                </div>
                <div className="d-flex justify-content-between text-sm">
                    <span className="text-gray-500">Sub Total</span>
                    <span className="fw-medium text-uppercase text-xs">₹{(order?.order.price - (order?.order.delivery_fee || 0)).toFixed(2)} </span>
                </div>
                <div className="pt-12 mt-4 border-top border-gray-100 d-flex justify-content-between align-items-center">
                    <span className="fw-bold text-heading">Grand Total</span>
                    <div className="text-end">
                        <span className="text-xl fw-bold text-main-600 d-block">₹{order?.order.price}</span>
                        <span className="text-xs text-gray-400 fw-normal">Inclusive of all taxes</span>
                    </div>
                </div>
            </div>
        </div>
    );

    const ShippingAddressSection = (
        <div className="bg-white rounded-16 border border-gray-100 shadow-sm p-24 mb-24">
            <h6 className="mb-16 pb-12 border-bottom border-gray-100">Shipping Address</h6>
            <div className="d-flex gap-12 align-items-start">
                <div className="bg-gray-50 p-8 rounded-8 text-main-600">
                    <i className="ph ph-map-pin text-xl" />
                </div>
                <div className="text-sm">
                    <p className="fw-bold text-heading mb-4">{order?.order.address.name}</p>
                    <p className="text-gray-500 mb-12">{order?.order.address.mobile}</p>
                    <div className="text-gray-600 leading-relaxed">
                        <p className="mb-0">{order?.order.address.house}, {order?.order.address.area}</p>
                        <p className="mb-0">{order?.order.address.city}, {order?.order.address.district_name}</p>
                        <p className="mb-0">{order?.order.address.state_name}, {order?.order.address.country_name}</p>
                        <p className="mt-8 mb-0 fw-medium">PIN: {order?.order.address.pincode}</p>
                        {order?.order.address.landmark && (
                            <p className="text-xs text-gray-400 mt-4 italic">Landmark: {order?.order.address.landmark}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const ShipmentSection = order?.shipment && order?.order?.order_status?.id >= 5 && (
        <div className="bg-white rounded-16 border border-gray-100 shadow-sm p-24 mb-24 overflow-hidden position-relative">
            <div className="position-absolute top-0 start-0 h-100" style={{ width: '4px', backgroundColor: order?.order.order_status?.colour || '#4CAF50' }}></div>
            <div className="d-flex align-items-center justify-content-between mb-20 pb-12 border-bottom border-gray-100">
                <div className="d-flex align-items-center gap-10">
                    <div className="p-8 rounded-8 d-flex align-items-center justify-content-center" style={{ backgroundColor: `${order?.order.order_status?.colour}25`, color: order?.order.order_status?.colour }}>
                        <i className="ph-fill ph-package text-xl"></i>
                    </div>
                    <h6 className="mb-0 text-md">Shipment Details</h6>
                </div>
                <span className="px-12 py-6 rounded-pill text-xs fw-bold border" style={{ backgroundColor: `${order?.order.order_status?.colour}20`, color: order?.order.order_status?.colour, borderColor: `${order?.order.order_status?.colour}40` }}>
                    {order?.order.order_status?.name}
                </span>
            </div>
            <div className="d-grid gap-12">
                {order.shipment.awb_code && (
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="text-sm text-gray-500">AWB Code</span>
                        <span className="text-sm fw-bold text-heading font-monospace">{order.shipment.awb_code}</span>
                    </div>
                )}
                {order.shipment.shipment_id && (
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="text-sm text-gray-500">Shipment ID</span>
                        <span className="text-sm fw-semibold text-heading">#{order.shipment.shipment_id}</span>
                    </div>
                )}
                
                <div className="row g-2 mt-8">
                    {order?.shipment && order?.order?.order_status?.id >= 6 && (
                        <div className="col">
                            <button 
                                type="button"
                                onClick={() => setShowTrackModal(true)}
                                className="btn btn-main w-100 d-flex align-items-center justify-content-center gap-8 py-12 rounded-12 transition-1"
                            >
                                <i className="ph-bold ph-truck text-lg" /> Track
                            </button>
                        </div>
                    )}

                    <div className="col">
                        {order.shipment.invoice_url && (
                            <a href={order.shipment.invoice_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-main w-100 d-flex align-items-center justify-content-center gap-8 py-12 rounded-12 transition-1">
                                <i className="ph-bold ph-file-text text-lg" /> Invoice
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <section className="shop py-40 bg-gray-50 min-vh-100">
            <div className="container container-lg">
                <div className="row gy-4">
                    {/* Left Column */}
                    <div className="col-xl-8 col-lg-7">
                        {isSmallOrder ? (
                            <>
                                {OrderItemsSection}
                                {OrderSummarySection}
                            </>
                        ) : (
                            OrderItemsSection
                        )}
                    </div>

                    {/* Right Column */}
                    <div className="col-xl-4 col-lg-5">
                        <div className="position-sticky" style={{ top: '24px' }}>
                            {!isSmallOrder && OrderSummarySection}
                            {ShippingAddressSection}
                            {ShipmentSection}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Modal Components */}
            <TrackingModal />
        </section>
    );
};

export default OrderDetails;